import { useState } from 'react'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { typography } from '@/styles/designTokens'
import { useNavigate } from 'react-router-dom'
import type { CartItem, CartItemModifier } from '@/shared/types/cart'
import type { MenuItem, MenuItemDetail, MenuModifierSet } from '@/shared/api/menu'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { PATHS } from '@/router/paths'
import TicketItemModifier from '@/components/menu/order-details/TicketItemModifier'
import { VisuallyHidden } from '@/components/common/ui/VisuallyHidden.styled'
import {
	ItemCard,
	ItemImage,
	ItemImagePlaceholder,
	ItemDetails,
	ItemNameGroup,
	ItemName,
	ItemModifiers,
	ItemPrice,
	ItemActions,
	ModifierToggleBtn,
	ItemCardEditBtn,
	QtyExpand,
	QtyExpandShell,
	QtyExpandTrack,
	QtyExpandLead,
	QtyExpandBox,
	QtyExpandBtn,
	QtyExpandTrashOutside,
	CartDivider,
	QTY_EXPAND_SHELL_CLASS,
	QTY_EXPAND_TRACK_CLASS,
	QTY_EXPAND_BOX_CLASS,
	QTY_EXPAND_TRASH_OUTSIDE_CLASS,
} from './CartItemRow.styled'

// Ember parity (cart-item-modifier.js subtotal + cart-item.js modifierTotal):
// Recursively sums modifier prices across the full modifier tree.
// unitPrice = modifier.price (flat) or itemBasePrice * modifier.percent (percent type).
// percent > 0 indicates a percent-type modifier (Ember: modifier.type === 'percent').
function calcModifierTotal(
	modifiers: CartItemModifier[],
	modifierSets: MenuModifierSet[],
	itemBasePrice: number,
): number {
	let total = 0
	for (const cartMod of modifiers) {
		let found: { price: number; percent: number; modifierSets: MenuModifierSet[] } | null = null
		for (const set of modifierSets) {
			if (set.ref === cartMod.modifierSetRef) {
				const mod = set.modifiers.find((m) => m.ref === cartMod.modifierRef)
				if (mod) {
					found = mod
					break
				}
			}
			for (const mod of set.modifiers) {
				const deepSet = mod.modifierSets.find((s) => s.ref === cartMod.modifierSetRef)
				if (deepSet) {
					const deepMod = deepSet.modifiers.find((m) => m.ref === cartMod.modifierRef)
					if (deepMod) {
						found = deepMod
						break
					}
				}
			}
			if (found) break
		}
		if (found) {
			const unitPrice = found.percent > 0 ? itemBasePrice * found.percent : found.price
			total += unitPrice * cartMod.quantity
			total += calcModifierTotal(cartMod.modifiers, found.modifierSets, itemBasePrice)
		}
	}
	return total
}

export interface CartItemRowProps {
	item: CartItem
	menuItem: MenuItem | undefined
	menuItemDetail?: MenuItemDetail
	locationRef: string
	onRemove?: (item: CartItem) => void
	onIncrement?: (item: CartItem) => void
	onDecrement?: (item: CartItem) => void
	currency?: string
	locale?: string
	showDivider?: boolean
	// When true: card is not clickable, edit/remove/quantity controls are not rendered.
	// Used by CheckoutCartPanel where the cart is display-only.
	readOnly?: boolean
}

const MODIFIER_COLLAPSE_THRESHOLD = 2

export function CartItemRow({
	item,
	menuItem,
	menuItemDetail,
	locationRef,
	onRemove,
	onIncrement,
	onDecrement,
	currency = 'USD',
	locale = 'en-US',
	showDivider = false,
	readOnly = false,
}: CartItemRowProps) {
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const [showAllModifiers, setShowAllModifiers] = useState(false)

	// Ember parity (ticket-item.hbs line 3): renders blank when menu record is not loaded —
	// never surfaces a raw ref string as a display name.
	const name = menuItem?.name ?? ''
	// Ember parity (ticket-item.js itemTotal): display price = base item price + all modifier prices.
	// Modifier detail only available once menuItemDetail has loaded — falls back to base price until then.
	const price = menuItem
		? (() => {
				const basePrice = menuItem.onlinePrice ?? menuItem.price
				const modifierTotal =
					menuItemDetail && item.modifiers.length > 0
						? calcModifierTotal(item.modifiers, menuItemDetail.modifierSets, basePrice)
						: 0
				return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
					(basePrice + modifierTotal) / 100,
				)
			})()
		: null

	// Special request counts toward the overflow threshold (Figma: shown only in expanded state).
	const totalRows = item.modifiers.length + (item.specialRequest ? 1 : 0)
	const hasOverflow = totalRows > MODIFIER_COLLAPSE_THRESHOLD
	const visibleModifiers =
		hasOverflow && !showAllModifiers
			? item.modifiers.slice(0, MODIFIER_COLLAPSE_THRESHOLD)
			: item.modifiers
	const hiddenCount = totalRows - MODIFIER_COLLAPSE_THRESHOLD

	// Stable ID linking the modifier toggle button to the modifier list it controls.
	// Doubles as an aria-describedby target — it holds the modifiers and, when the
	// list is expanded, the special request.
	const modifierListId = `modifiers-${item.id}`
	const priceId = `cart-item-price-${item.id}`
	// ItemCardEditBtn carries the accessible name "Edit {name}"; describedby adds the
	// modifier / special-request text and the price so AT announces them on focus.
	// Quantity is deliberately excluded — ItemActions already exposes it and the
	// aria-live region announces it on change; adding it here would double-announce.
	// modifierListId is only included when there is modifier or special-request content;
	// if the container holds only the item name, it duplicates the accessible name.
	const hasModifierContent = item.modifiers.length > 0 || !!item.specialRequest
	const describedBy = [hasModifierContent ? modifierListId : null, price ? priceId : null]
		.filter(Boolean)
		.join(' ')

	function handleCardClick() {
		if (readOnly) return
		void navigate(PATHS.editItem(locationRef, item.id))
	}

	function handleRemoveClick(e: React.MouseEvent) {
		e.stopPropagation()
		onRemove?.(item)
	}

	function handleDecrementClick(e: React.MouseEvent) {
		e.stopPropagation()
		// Blur on mouse click only (e.detail > 0) so the pill collapses when the mouse moves away.
		// Keyboard-triggered clicks have e.detail === 0 — blur is skipped so focus is preserved.
		if (e.detail > 0) (e.currentTarget as HTMLElement).blur()
		if (item.quantity === 1) {
			onRemove?.(item)
		} else {
			onDecrement?.(item)
		}
	}

	function handleIncrementClick(e: React.MouseEvent) {
		e.stopPropagation()
		if (e.detail > 0) (e.currentTarget as HTMLElement).blur()
		onIncrement?.(item)
	}

	function handleToggleModifiers(e: React.MouseEvent) {
		e.stopPropagation()
		setShowAllModifiers((prev) => !prev)
	}

	const leadAriaLabel =
		item.quantity === 1
			? t('component:cartItemRow:remove:label', { name })
			: t('component:cartItemRow:decrement:label', { name })

	return (
		<div role="listitem" data-testid="cart-item-row-group">
			<ItemCard onClick={readOnly ? undefined : handleCardClick} data-testid="cart-item-row">
				{/* Overlay button — full-cover <button> for keyboard and AT access to the card-edit
				    action. Sits at z-index:0 behind all content (z-index:1). Mouse users click
				    directly on the card or its content; keyboard/AT users Tab to this button. */}
				{!readOnly && (
					<ItemCardEditBtn
						type="button"
						aria-label={t('component:cartItemRow:edit:label', { name })}
						aria-describedby={describedBy}
						onClick={(e) => {
							e.stopPropagation()
							handleCardClick()
						}}
						data-testid="cart-item-edit-btn"
					/>
				)}
				{menuItem?.imageUrl800x800 ? (
					<ItemImage src={menuItem.imageUrl800x800} alt="" loading="lazy" />
				) : (
					<ItemImagePlaceholder aria-hidden="true" />
				)}

				<ItemDetails>
					<ItemNameGroup id={modifierListId}>
						<ItemName>{name}</ItemName>
						{item.modifiers.length > 0 &&
							menuItemDetail &&
							visibleModifiers.map((modifier) => (
								<TicketItemModifier
									key={modifier.modifierRef}
									modifier={modifier}
									menuItem={menuItemDetail}
									itemBasePrice={
										menuItemDetail.onlinePrice ?? menuItemDetail.price
									}
									locale={locale}
									currency={currency}
								/>
							))}
						{item.specialRequest && (!hasOverflow || showAllModifiers) && (
							<ItemModifiers>
								{t('component:cartItemRow:specialRequest:label', {
									text: item.specialRequest,
								})}
							</ItemModifiers>
						)}
					</ItemNameGroup>
					{hasOverflow && (
						<ModifierToggleBtn
							type="button"
							onClick={handleToggleModifiers}
							aria-expanded={showAllModifiers}
							aria-controls={modifierListId}
							aria-label={
								showAllModifiers
									? t('component:cartItemRow:modifiers:showLess:ariaLabel', {
											name,
										})
									: t('component:cartItemRow:modifiers:showMore:ariaLabel', {
											name,
											count: hiddenCount,
										})
							}
						>
							{showAllModifiers
								? t('component:cartItemRow:modifiers:showLess')
								: t('component:cartItemRow:modifiers:showMore', {
										count: hiddenCount,
									})}
						</ModifierToggleBtn>
					)}
					{price && <ItemPrice id={priceId}>{price}</ItemPrice>}
				</ItemDetails>

				{readOnly && (
					<ItemActions>
						{/* QtyExpandBox is a div — aria-label on a role-less element is ignored
						    by AT, so the accessible text is provided by a visually-hidden span
						    and the digit itself is hidden to avoid a duplicate announcement. */}
						<QtyExpandBox className={QTY_EXPAND_BOX_CLASS} readOnly>
							<VisuallyHidden>
								{t('component:quantitySelector:value')}: {item.quantity}
							</VisuallyHidden>
							<span aria-hidden="true">{item.quantity}</span>
						</QtyExpandBox>
					</ItemActions>
				)}
				{!readOnly && (
					<ItemActions data-testid="item-actions">
						{/* CSS-driven expand — no JS hover state.
						    QtyExpand starts at 40px (count only), expands to 120px on :hover/:focus-within.
						    The track slides left-to-right revealing ± buttons (olo-demo parity). */}
						<QtyExpand
							role="group"
							aria-label={t('component:cartItemRow:quantityGroup:label', { name })}
							onClick={(e) => e.stopPropagation()}
						>
							{/* Visually-hidden live region — announces full "Quantity: X" phrase
							    on +/− clicks. aria-live on the visible QtyExpandBox would only
							    announce the bare number ("3"), which gives no context to the user.
							    Placed outside QtyExpandShell so overflow:hidden doesn't affect it. */}
							<VisuallyHidden aria-live="polite" aria-atomic="true">
								{t('component:quantitySelector:value')}: {item.quantity}
							</VisuallyHidden>

							{/* Trash icon shown outside the pill at collapsed size — hides on expand.
							    Mouse-only affordance: lead button inside pill provides the same action.
							    Excluded from keyboard/AT navigation to avoid focus landing on a
							    CSS-hidden element when the pill is expanded via :focus-within. */}
							<QtyExpandTrashOutside
								className={QTY_EXPAND_TRASH_OUTSIDE_CLASS}
								aria-hidden="true"
								tabIndex={-1}
								onClick={handleRemoveClick}
								data-testid="qty-trash-outside"
							>
								<DeleteOutlinedIcon sx={{ fontSize: typography.fontSizeIconMd }} />
							</QtyExpandTrashOutside>

							<QtyExpandShell className={QTY_EXPAND_SHELL_CLASS}>
								<QtyExpandTrack className={QTY_EXPAND_TRACK_CLASS}>
									{/* Lead button: trash (qty=1) or minus (qty>1) */}
									<QtyExpandLead>
										<QtyExpandBtn
											type="button"
											aria-label={leadAriaLabel}
											onClick={handleDecrementClick}
											data-testid="qty-lead-btn"
										>
											{item.quantity === 1 ? (
												<DeleteOutlinedIcon
													sx={{ fontSize: typography.fontSizeIconMd }}
												/>
											) : (
												<RemoveIcon
													sx={{ fontSize: typography.fontSizeIconMd }}
												/>
											)}
										</QtyExpandBtn>
									</QtyExpandLead>

									{/* Hidden from AT: the aria-live region above is the single
									    source of the quantity announcement. aria-label here would
									    be ignored anyway (role-less div) and the bare digit adds
									    no context when read. */}
									<QtyExpandBox
										className={QTY_EXPAND_BOX_CLASS}
										aria-hidden="true"
									>
										{item.quantity}
									</QtyExpandBox>

									<QtyExpandBtn
										type="button"
										aria-label={t('component:cartItemRow:increment:label', {
											name,
										})}
										onClick={handleIncrementClick}
										data-testid="qty-plus-btn"
									>
										<AddIcon sx={{ fontSize: typography.fontSizeIconMd }} />
									</QtyExpandBtn>
								</QtyExpandTrack>
							</QtyExpandShell>
						</QtyExpand>
					</ItemActions>
				)}
			</ItemCard>
			{showDivider && <CartDivider />}
		</div>
	)
}
